/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   11/11/21
 */

import React from "react";
import DataContainer from "./DataContainer";
import {useDispatch, useSelector} from "react-redux";
import {State} from "../state/store";
import {EmployeeHierarchyQuery, EmployeeHierarchyQueryVars, EmployeeQueryResponse} from "../graphql/queries/employees";
import {useClient, useQuery} from "urql";
import {set} from "../state/reducers/calendar";
import {set as setEmployeesFilter} from "../state/reducers/employees-filter";
import {defaultSettings, set as setSettings} from "../state/reducers/settings";
import {SavedCalendarQuery, SavedCalendarQueryVars, SavedCalendarResponse} from "../graphql/queries/calendars";
import {useParams} from "react-router-dom";
import {EmployeesFilterItem} from "../interfaces/Global";
import {Stack} from "@mui/material";
import HeroMessage from "../components/HeroMessage";
import LoadingHero from "../components/LoadingHero";

export default function Container() {

	const { id } = useParams<{id?: string}>();

	const client = useClient();
	const defaultCalendarId = useSelector((s:State) => s.defaultCalendarId);
	const user = useSelector((s:State) => s.user);
	const filter = useSelector((s:State) => s.employeesFilter);
	const dispatch = useDispatch();

	const loadCalendarId = id ? Number(id) : defaultCalendarId;

	const [fetching, setFetching] = React.useState(!!loadCalendarId);
	const [error, setError] = React.useState<{title: string, message: string}|null>(null);

	const [userEmployeeResult] = useQuery<EmployeeQueryResponse, EmployeeHierarchyQueryVars>({
		query: EmployeeHierarchyQuery,
		pause: filter.length > 0,
		variables: {
			id: user?.id as string,
			isActive: true
		}
	});

	React.useEffect(() => {
		if (loadCalendarId) {
			setFetching(true)
			client.query<SavedCalendarResponse, SavedCalendarQueryVars>(SavedCalendarQuery, { id: loadCalendarId })
				.toPromise().then(result => {
				const calendar = result.data?.savedCalendar;
				if (calendar) {
					setError(null);
					dispatch(setEmployeesFilter(calendar.config?.employeesFilter || []));
					dispatch(setSettings({ ...defaultSettings, ...(calendar.config?.settings || {})}));
					dispatch(set({
						id: calendar.id,
						title: calendar.title
					}));
				} else {
					setError({ title: "Calendar Not Found", message: "We couldn't locate the calendar with ID " + loadCalendarId });
				}
			}).catch(err => setError({ title: "Request Error", message: err.message }))
				.finally(() => setFetching(false));
		}
	}, [dispatch, loadCalendarId, client]);

	React.useEffect(() => {
		if (filter.length === 0 && !userEmployeeResult.fetching && user?.id) {
			let employeesFilter: EmployeesFilterItem[] = [ ["and", "id", [ user.id ]] ];
			if ((userEmployeeResult?.data?.employee.directReports?.length||0) > 0) {
				employeesFilter = [ ["and", "managerId", [ user.id ]] ];
			} else if (userEmployeeResult.data?.employee?.manager?.id) {
				employeesFilter = [ ["and", "managerId", [ userEmployeeResult.data.employee.manager.id as string ]] ];
			}
			dispatch(setEmployeesFilter(employeesFilter));
		}
	}, [dispatch, filter, user, userEmployeeResult]);

	return (
		<Stack>
			{error &&
				<HeroMessage
					title={error.title}
					message={error.message}
				/>
			}
			{!error && fetching &&
				<LoadingHero />
			}
			{!error && !fetching &&
				<DataContainer />
			}
		</Stack>
	);

}